import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import KpiSummaryBar from '../components/KpiSummaryBar';
import ContactsControlsBar from '../components/ContactsControlsBar';
import ContactsTableView from '../components/ContactsTableView';
import ContactsCardView from '../components/ContactsCardView';
import ContactsKanbanView from '../components/ContactsKanbanView';
import ContactDashboard from './ContactDashboard';
import {
  archiveFoundationContact,
  createFoundationContact,
  getFoundationContact,
  listFoundationContacts,
  updateFoundationContact,
} from '../services/contactsFoundationApi';
import type {
  ContactMutation,
  ContactStatus,
  ContactType,
  FoundationContact,
} from '../services/contactsFoundationApi';
import {
  fromLegacyStatus,
  fromLegacyType,
  toLegacyContact,
} from '../services/contactViewModel';
import type { Contact, ContactFilters } from '../../../config/api';

type ContactsView = 'table' | 'card' | 'kanban';

type ContactFormState = {
  name: string;
  type: ContactType;
  status: ContactStatus;
  email: string;
  phone: string;
  address: string;
  tags: string;
  notes: string;
};

const emptyForm: ContactFormState = {
  name: '',
  type: 'person',
  status: 'lead',
  email: '',
  phone: '',
  address: '',
  tags: '',
  notes: '',
};

function toForm(contact: FoundationContact | null): ContactFormState {
  if (!contact) return emptyForm;
  return {
    name: contact.name,
    type: contact.type,
    status: contact.status,
    email: contact.email || '',
    phone: contact.phone || '',
    address: contact.address || '',
    tags: contact.tags.join(', '),
    notes: contact.notes || '',
  };
}

function toMutation(form: ContactFormState): ContactMutation {
  return {
    name: form.name.trim(),
    type: form.type,
    status: form.status,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    address: form.address.trim() || null,
    tags: form.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean),
    notes: form.notes.trim() || null,
  };
}

function getSearchFromLocation(): string {
  return new URLSearchParams(window.location.search).get('search') || '';
}

const ContactsDashboard: React.FC = () => {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [currentView, setCurrentView] = useState<ContactsView>('card');
  const [contactFilters, setContactFilters] = useState<ContactFilters>({});
  const [searchQuery, setSearchQuery] = useState(getSearchFromLocation());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = keycloak?.token;

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listFoundationContacts(token, {
        search: searchQuery,
        status: contactFilters.status ? fromLegacyStatus(contactFilters.status) : '',
        type: contactFilters.contact_type ? fromLegacyType(contactFilters.contact_type) : '',
      });

      setContacts(result.contacts.map(toLegacyContact));
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load contacts');
    } finally {
      setLoading(false);
    }
  }, [token, searchQuery, contactFilters.status, contactFilters.contact_type]);

  useEffect(() => {
    const onPopState = () => setSearchQuery(getSearchFromLocation());
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadContacts();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadContacts]);

  const kpiMetrics = useMemo(() => ({
    totalContacts: contacts.length,
    activeClients: contacts.filter((contact) => contact.status === 'converted').length,
    newLeads: contacts.filter((contact) => {
      if (contact.status !== 'lead') return false;
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return new Date(contact.created_at) >= thirtyDaysAgo;
    }).length,
    avgResponseTime: 24,
  }), [contacts]);

  const handleFilterChange = (newFilters: Partial<ContactFilters>) => {
    if (Object.keys(newFilters).length === 0) {
      setContactFilters({});
      return;
    }
    setContactFilters((current) => ({ ...current, ...newFilters }));
  };

  const handleStatusChange = async (contactId: string, newStatus: string) => {
    const nextStatus = fromLegacyStatus(newStatus);
    const saved = await updateFoundationContact(token, contactId, { status: nextStatus });
    setContacts((current) => current.map((contact) => (
      contact.id === saved.id ? toLegacyContact(saved) : contact
    )));
  };

  const handleBulkDelete = async (contactIds: string[]) => {
    for (const contactId of contactIds) {
      await archiveFoundationContact(token, contactId);
    }
    await loadContacts();
  };

  const commonProps = {
    contacts,
    loading,
    error,
    searchQuery,
    filters: contactFilters,
    onContactClick: (contact: Contact) => navigate(`/contacts/${encodeURIComponent(contact.id)}`),
  };

  const renderContactsView = () => {
    switch (currentView) {
      case 'table':
        return <ContactsTableView {...commonProps} onDeleteContacts={handleBulkDelete} />;
      case 'kanban':
        return <ContactsKanbanView {...commonProps} onStatusChange={handleStatusChange} />;
      case 'card':
      default:
        return <ContactsCardView {...commonProps} />;
    }
  };

  return (
    <Routes>
      <Route
        index
        element={(
          <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
            <KpiSummaryBar metrics={kpiMetrics} />
            <ContactsControlsBar
              currentView={currentView}
              onViewChange={setCurrentView}
              filters={contactFilters}
              onFilterChange={handleFilterChange}
            />
            <div className="px-6 py-6">
              <div className="max-w-7xl mx-auto">
                {renderContactsView()}
              </div>
            </div>
          </div>
        )}
      />
      <Route
        path="new"
        element={(
          <ContactEditorPage
            token={token}
            onSaved={(contactId) => {
              void loadContacts();
              navigate(`/contacts/${encodeURIComponent(contactId)}`);
            }}
          />
        )}
      />
      <Route
        path=":contactId"
        element={<ContactDashboard />}
      />
      <Route path="*" element={<Navigate to="/contacts" replace />} />
    </Routes>
  );
};

function ContactEditorPage({
  token,
  knownContacts = [],
  onSaved,
}: {
  token: string | undefined;
  knownContacts?: FoundationContact[];
  onSaved: (contactId: string) => void;
}) {
  const navigate = useNavigate();
  const { contactId } = useParams();
  const [contact, setContact] = useState<FoundationContact | null>(
    contactId ? knownContacts.find((item) => item.id === contactId) || null : null,
  );
  const [form, setForm] = useState<ContactFormState>(() => toForm(contact));
  const [loading, setLoading] = useState(Boolean(contactId && !contact));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contactId) {
      setContact(null);
      setForm(emptyForm);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getFoundationContact(token, contactId)
      .then((loaded) => {
        if (cancelled) return;
        setContact(loaded);
        setForm(toForm(loaded));
      })
      .catch((loadError) => {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : 'Unable to load contact');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, contactId]);

  const updateForm = <Key extends keyof ContactFormState>(key: Key, value: ContactFormState[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const mutation = toMutation(form);
      if (!mutation.name) throw new Error('Contact name is required.');
      const saved = contact
        ? await updateFoundationContact(token, contact.id, mutation)
        : await createFoundationContact(token, mutation);
      setContact(saved);
      setForm(toForm(saved));
      onSaved(saved.id);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen px-6 py-6">
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              {contact ? contact.name : 'New Contact'}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              General contact profile
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/contacts')}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Back to Contacts
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-gray-500 dark:text-gray-400">Loading contact...</div>
        ) : (
          <div className="p-6 space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 text-red-800 dark:text-red-200">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ContactField label="Name" value={form.name} onChange={(value) => updateForm('name', value)} />
              <label className="grid gap-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                Type
                <select
                  value={form.type}
                  onChange={(event) => updateForm('type', event.target.value as ContactType)}
                  className="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 font-normal"
                >
                  <option value="person">Individual</option>
                  <option value="organization">Company</option>
                </select>
              </label>
              <label className="grid gap-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                Status
                <select
                  value={form.status}
                  onChange={(event) => updateForm('status', event.target.value as ContactStatus)}
                  className="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 font-normal"
                >
                  <option value="lead">Lead</option>
                  <option value="qualified">Qualified</option>
                  <option value="active">Converted</option>
                  <option value="archived">Archived</option>
                </select>
              </label>
              <ContactField label="Email" value={form.email} onChange={(value) => updateForm('email', value)} />
              <ContactField label="Phone" value={form.phone} onChange={(value) => updateForm('phone', value)} />
              <ContactField label="Tags" value={form.tags} onChange={(value) => updateForm('tags', value)} />
            </div>
            <ContactField label="Address" value={form.address} onChange={(value) => updateForm('address', value)} />
            <label className="grid gap-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              Notes
              <textarea
                value={form.notes}
                onChange={(event) => updateForm('notes', event.target.value)}
                rows={6}
                className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 font-normal"
              />
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg font-medium disabled:opacity-60"
              >
                {saving ? 'Saving...' : 'Save Contact'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="grid gap-1 text-sm font-medium text-gray-700 dark:text-gray-200">
      {label}
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 font-normal"
      />
    </label>
  );
}

export default ContactsDashboard;
